import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingButtonComponent } from '@components/loading-button/loading-button.component';
import { LoadingComponent } from '@components/loading/loading.component';
import { APP_CONSTANTS } from '@constants-globals/constants';
import { MESSAGES } from '@constants-globals/messages';
import { UserSession } from '@models/session';
import { RepresentativeUser } from '@models/user';
import { NgIcon } from '@ng-icons/core';
import { ToastService } from '@services/toast.service';
import { TokenStorageService } from '@services/token-storage.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
} from 'rxjs';
import { RepresentativeService } from 'src/app/api/services/representatives.service';

@Component({
  selector: 'app-user-company-members',
  imports: [
    NgIcon,
    LoadingComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingButtonComponent,
  ],
  templateUrl: './user-company-members.component.html',
  styles: ``,
})
export class UserCompanyMembersComponent implements OnInit {
  representatives: RepresentativeUser[] = [];
  modalAddRepresentative: boolean = false;
  searchControl!: FormControl;
  searchResults: RepresentativeUser[] = [];
  selectedUserToAdd: RepresentativeUser | null = null;
  isSearching: boolean = false;
  isAdding: boolean = false;
  isLoading: boolean = true;
  idCompany: string | null = null;
  modalConfirmRemove: boolean = false;
  userToRemove: RepresentativeUser | null = null;
  isRemoving: boolean = false;
  userSession: UserSession | null = null;

  constructor(
    private route: ActivatedRoute,
    private representativeService: RepresentativeService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private tokenStorageService: TokenStorageService,
  ) {
    this.searchControl = this.fb.control('', []);
  }

  ngOnInit(): void {
    this.setupSearchControl();
    const session = this.tokenStorageService.getAuth();
    this.userSession = session?.user || null;

    this.route.parent?.params.subscribe((params) => {
      this.idCompany = params['idCompany'] || params['id'];
      if (this.idCompany) {
        this.loadRepresentatives();
      } else {
        this.isLoading = false;
      }
    });
  }

  private setupSearchControl(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        filter(
          (email): email is string =>
            typeof email === 'string' && email.trim().length > 0
        ),
        switchMap((email: string) => {
          this.selectedUserToAdd = null;
          if (this.searchControl.invalid) {
            this.searchResults = [];
            return of([]);
          }

          this.isSearching = true;
          return this.representativeService
            .searchByParam(email)
            .pipe(map((response) => response.dto.users));
        })
      )
      .subscribe({
        next: (users: RepresentativeUser[]) => {
          this.searchResults = users.filter(
            (user) => !this.representatives.some((rep) => rep.id === user.id)
          );
          this.isSearching = false;
        },
        error: (err) => {
          this.isSearching = false;
          this.searchResults = [];
        },
      });
  }

  loadRepresentatives(): void {
    if (!this.idCompany) return;

    this.isLoading = true;
    this.representativeService.getByIdCompany(this.idCompany).subscribe({
      next: (res) => {
        this.representatives = res.dto.users;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }

  openAddRepresentativeModal(): void {
    this.modalAddRepresentative = true;
    this.searchControl.setValue('');
    this.searchResults = [];
    this.selectedUserToAdd = null;
  }

  closeAddRepresentativeModal(): void {
    this.modalAddRepresentative = false;
    this.searchControl.setValue('');
    this.searchResults = [];
    this.selectedUserToAdd = null;
  }

  selectUser(user: RepresentativeUser): void {
    if (this.selectedUserToAdd?.id === user.id) {
      this.selectedUserToAdd = null;
    } else {
      this.selectedUserToAdd = user;
    }
  }

  removeRepresentative(idUser: string): void {
    const user = this.representatives.find((rep) => rep.id === idUser);
    if (user) {
      this.openConfirmRemoveModal(user);
    }
  }

  confirmRemoveRepresentative(): void {
    if (!this.userToRemove || !this.idCompany) {
      this.toastService.showMessages('error', [
        'Faltan datos para realizar la operación de eliminación.',
      ]);
      return;
    }

    this.isRemoving = true;
    const userFirstName = this.userToRemove.firstName;
    const payload = {
      idUser: this.userToRemove.id,
      idCompany: this.idCompany,
    };

    this.representativeService.remove(payload).subscribe({
      next: () => {
        this.isRemoving = false;
        this.closeConfirmRemoveModal();
        this.loadRepresentatives();
        this.toastService.showMessages('success', [
          `El usuario ${userFirstName} fue removido como representante.`,
        ]);
      },
      error: (err) => {
        this.isRemoving = false;
        const errorMsg =
          'No se pudo quitar al representante. Inténtalo de nuevo.';
        this.toastService.showMessages('error', [errorMsg]);
      },
    });
  }

  openConfirmRemoveModal(user: RepresentativeUser): void {
    this.userToRemove = user;
    this.modalConfirmRemove = true;
  }

  closeConfirmRemoveModal(): void {
    this.modalConfirmRemove = false;
    this.userToRemove = null;
  }

  addRepresentativeInline(user: RepresentativeUser): void {
    this.selectedUserToAdd = user;
    this.addRepresentative();
  }

  addRepresentative(): void {
    if (!this.selectedUserToAdd || !this.idCompany) {
      this.toastService.showMessages('error', [
        'Faltan datos para realizar la operación.',
      ]);
      return;
    }

    this.isAdding = true;
    const userFirstName = this.selectedUserToAdd.firstName;
    const payload = {
      idUser: this.selectedUserToAdd.id,
      idCompany: this.idCompany,
    };

    this.representativeService.insert(payload).subscribe({
      next: () => {
        this.isAdding = false;
        this.closeAddRepresentativeModal();
        this.loadRepresentatives();

        this.toastService.showMessages('success', [
          `El usuario ${userFirstName} ha sido agregado como representante.`,
        ]);
      },
      error: (err) => {
        this.isAdding = false;

        const errorMsg =
          'No se pudo agregar al representante. Inténtalo de nuevo.';
        this.toastService.showMessages('error', [errorMsg]);
      },
    });
  }
  getUserImage(user: RepresentativeUser): string {
    return user.image || APP_CONSTANTS.DEFAULT_IMAGE_PROFILE;
  }
}
